import { useState, useEffect, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../contexts/AuthContext';
import { apiClient } from '../utils/apiClient';

interface BuildInfo {
  hash: string;
  timestamp: string;
}

// Small X logo
const XLogoSmall = () => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="currentColor"
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Wallet icon
const WalletIcon = () => (
  <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
    <path d="M21 18v1c0 1.1-.9 2-2 2H5c-1.11 0-2-.9-2-2V5c0-1.1.89-2 2-2h14c1.1 0 2 .9 2 2v1h-9c-1.11 0-2 .9-2 2v8c0 1.1.89 2 2 2h9zm-9-2h10V8H12v8zm4-2.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z" />
  </svg>
);

function truncateAddress(address: string): string {
  if (!address || address.length < 10) return address;
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

async function getSwBuildInfo(): Promise<BuildInfo | null> {
  try {
    const response = await fetch('/build-info.json', { cache: 'no-store' });
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

async function getPageBuildInfo(): Promise<BuildInfo | null> {
  try {
    const response = await fetch('/build-info.json');
    if (!response.ok) return null;
    return await response.json();
  } catch {
    return null;
  }
}

/**
 * Arcade-wide settings page.
 * Contains: Account info, Network status, Debug info, Account deletion.
 */
function ArcadeSettings() {
  const navigate = useNavigate();
  const { isAuthenticated, user, signOut, updateDisplayName } = useAuthContext();

  // Build info
  const [buildInfo, setBuildInfo] = useState<BuildInfo | null>(null);
  const [swBuildInfo, setSwBuildInfo] = useState<BuildInfo | null>(null);

  // Online status
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Display name editing
  const [isEditingName, setIsEditingName] = useState(false);
  const [displayName, setDisplayName] = useState(user?.displayName || '');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  // Delete account modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Sign out confirmation
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Fetch build info
  useEffect(() => {
    getPageBuildInfo().then(setBuildInfo);
    getSwBuildInfo().then(setSwBuildInfo);
  }, []);

  // Update display name when user changes
  useEffect(() => {
    if (user?.displayName) {
      setDisplayName(user.displayName);
    }
  }, [user?.displayName]);

  const handleSaveDisplayName = async () => {
    if (!displayName.trim() || displayName.length < 2 || displayName.length > 20) {
      setSaveError('Display name must be 2-20 characters');
      return;
    }

    setIsSaving(true);
    setSaveError(null);

    try {
      await apiClient.updateProfile({ display_name: displayName.trim() });
      updateDisplayName(displayName.trim()); // Update local auth state
      setIsEditingName(false);
    } catch (err) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteAccount = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await apiClient.deleteAccount();
      await signOut();
      alert('Your account has been deleted.');
      navigate('/');
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete account');
      setIsDeleting(false);
    }
  };

  const handleSignOut = async () => {
    setShowSignOutConfirm(false);
    await signOut();
    navigate('/');
  };

  return (
    <div style={styles.container}>
      <div style={styles.page}>
        {/* Header */}
        <div style={styles.header}>
          <button style={styles.backButton} onClick={() => navigate(-1)}>
            ← Back
          </button>
          <h1 style={styles.title}>Account</h1>
          <div style={styles.headerSpacer} />
        </div>

        {/* Account Section */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Account</div>

          {isAuthenticated && user ? (
            <>
              {/* Auth Type Indicator */}
              <div style={styles.accountRow}>
                <span style={styles.accountLabel}>Signed in with</span>
                <div style={styles.authBadge}>
                  {user.authType === 'twitter' ? (
                    <>
                      <XLogoSmall />
                      <span>@{user.twitterHandle}</span>
                    </>
                  ) : (
                    <>
                      <WalletIcon />
                      <span>{truncateAddress(user.walletAddress || '')}</span>
                    </>
                  )}
                </div>
              </div>

              {/* Display Name */}
              <div style={styles.accountRow}>
                <span style={styles.accountLabel}>Display Name</span>
                {isEditingName ? (
                  <div style={styles.editNameRow}>
                    <input
                      type="text"
                      value={displayName}
                      onChange={(e) => setDisplayName(e.target.value)}
                      maxLength={20}
                      style={styles.nameInput}
                      disabled={user.authType === 'twitter'}
                    />
                    <button
                      style={styles.saveButton}
                      onClick={handleSaveDisplayName}
                      disabled={isSaving || user.authType === 'twitter'}
                    >
                      {isSaving ? '...' : 'Save'}
                    </button>
                    <button
                      style={styles.cancelEditButton}
                      onClick={() => {
                        setIsEditingName(false);
                        setDisplayName(user.displayName);
                        setSaveError(null);
                      }}
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <div style={styles.nameDisplay}>
                    <span style={styles.nameText}>{user.displayName}</span>
                    {user.authType === 'wallet' && (
                      <button
                        style={styles.editButton}
                        onClick={() => setIsEditingName(true)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>
              {saveError && <div style={styles.errorText}>{saveError}</div>}
              {user.authType === 'twitter' && isEditingName && (
                <div style={styles.hintText}>
                  Twitter users cannot change display name
                </div>
              )}

              {/* Sign Out */}
              <div style={styles.accountRow}>
                <span style={styles.accountLabel}>Session</span>
                <button
                  style={styles.signOutButton}
                  onClick={() => setShowSignOutConfirm(true)}
                >
                  Sign Out
                </button>
              </div>
            </>
          ) : (
            <div style={styles.notSignedIn}>
              <span style={styles.notSignedInText}>Not signed in</span>
              <span style={styles.hintText}>
                Sign in to save scores and compete on leaderboards
              </span>
            </div>
          )}
        </div>

        {/* Network Status */}
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Network</div>
          <div style={styles.statusRow}>
            <div style={styles.statusContent}>
              <span style={styles.statusLabel}>Status</span>
              {!isOnline && (
                <span style={styles.offlineHint}>
                  Leaderboards and replays unavailable offline
                </span>
              )}
            </div>
            <div style={styles.statusIndicator}>
              <div
                style={{
                  ...styles.statusDot,
                  backgroundColor: isOnline ? '#4ade80' : '#666',
                }}
              />
              <span style={styles.statusText}>
                {isOnline ? 'Online' : 'Offline'}
              </span>
            </div>
          </div>
        </div>

        {/* Debug Section */}
        {buildInfo && (
          <div style={styles.section}>
            <div style={styles.sectionTitle}>About</div>
            <div style={styles.debugRow}>
              <span style={styles.debugLabel}>Version</span>
              <span style={styles.debugValue}>{buildInfo.hash}</span>
            </div>
            {swBuildInfo && (
              <div style={styles.debugRow}>
                <span style={styles.debugLabel}>Service Worker</span>
                <span style={{
                  ...styles.debugValue,
                  color: swBuildInfo.hash === buildInfo.hash ? '#888' : '#f59e0b',
                }}>
                  {swBuildInfo.hash}
                  {swBuildInfo.hash !== buildInfo.hash && ' (update pending)'}
                </span>
              </div>
            )}
            <div style={styles.debugRow}>
              <span style={styles.debugLabel}>Built</span>
              <span style={styles.debugValue}>
                {new Date(buildInfo.timestamp).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })} {new Date(buildInfo.timestamp).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit',
                  hour12: true,
                })}
              </span>
            </div>
          </div>
        )}

        {/* Danger Zone - Only show if authenticated */}
        {isAuthenticated && (
          <div style={styles.dangerSection}>
            <div style={styles.dangerTitle}>Danger Zone</div>
            <div style={styles.dangerContent}>
              <div style={styles.dangerInfo}>
                <span style={styles.dangerLabel}>Delete Account</span>
                <span style={styles.dangerHint}>
                  Permanently delete your account and all data
                </span>
              </div>
              <button
                style={styles.deleteButton}
                onClick={() => setShowDeleteModal(true)}
              >
                Delete
              </button>
            </div>
          </div>
        )}

        {/* Sign Out Confirmation Modal */}
        {showSignOutConfirm && (
          <div style={styles.modalOverlay} onClick={() => setShowSignOutConfirm(false)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <h3 style={styles.modalTitle}>Sign Out?</h3>
              <p style={styles.modalText}>Are you sure you want to sign out?</p>
              <div style={styles.modalButtons}>
                <button
                  style={styles.modalCancelButton}
                  onClick={() => setShowSignOutConfirm(false)}
                >
                  Cancel
                </button>
                <button style={styles.modalConfirmButton} onClick={handleSignOut}>
                  Sign Out
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Delete Account Confirmation Modal */}
        {showDeleteModal && (
          <div style={styles.modalOverlay} onClick={() => !isDeleting && setShowDeleteModal(false)}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalIcon}>!</div>
              <h3 style={styles.modalTitle}>Delete Account?</h3>
              <div style={styles.modalBody}>
                <p style={styles.modalText}>This will permanently delete:</p>
                <ul style={styles.modalList}>
                  <li>Your account and profile</li>
                  <li>Your game scores</li>
                  <li>Your replay sessions</li>
                </ul>
                <p style={styles.modalWarning}>This action cannot be undone.</p>
              </div>
              {deleteError && <div style={styles.errorBox}>{deleteError}</div>}
              <div style={styles.modalButtons}>
                <button
                  style={styles.modalCancelButton}
                  onClick={() => setShowDeleteModal(false)}
                  disabled={isDeleting}
                >
                  Cancel
                </button>
                <button
                  style={{
                    ...styles.modalDeleteButton,
                    ...(isDeleting ? styles.buttonDisabled : {}),
                  }}
                  onClick={handleDeleteAccount}
                  disabled={isDeleting}
                >
                  {isDeleting ? 'Deleting...' : 'Delete Account'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, CSSProperties> = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FEAC64', // Orange theme
    display: 'flex',
    justifyContent: 'center',
    padding: '0',
  },
  page: {
    width: '100%',
    maxWidth: '500px',
    padding: '16px',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '24px',
    paddingTop: '8px',
  },
  backButton: {
    backgroundColor: 'rgba(0, 0, 0, 0.2)',
    color: '#333',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  title: {
    color: '#333',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: 0,
  },
  headerSpacer: {
    width: '80px',
  },
  section: {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
    border: '2px solid #333',
  },
  sectionTitle: {
    color: '#333',
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  accountRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '12px 0',
    borderBottom: '1px solid rgba(0, 0, 0, 0.2)',
  },
  accountLabel: {
    color: '#333',
    fontSize: '14px',
  },
  authBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#000',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  nameDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  nameText: {
    color: '#000',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  editButton: {
    backgroundColor: '#000',
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '4px',
    padding: '4px 8px',
    fontSize: '12px',
    cursor: 'pointer',
  },
  editNameRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  nameInput: {
    backgroundColor: '#fff',
    border: '2px solid #333',
    borderRadius: '4px',
    padding: '6px 10px',
    color: '#000',
    fontSize: '14px',
    width: '120px',
  },
  saveButton: {
    backgroundColor: '#1e8449',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  cancelEditButton: {
    backgroundColor: 'transparent',
    color: '#333',
    border: 'none',
    padding: '6px',
    fontSize: '14px',
    cursor: 'pointer',
  },
  signOutButton: {
    backgroundColor: '#000',
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '6px',
    padding: '6px 12px',
    fontSize: '12px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  notSignedIn: {
    padding: '16px 0',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  notSignedInText: {
    color: '#333',
    fontSize: '14px',
  },
  hintText: {
    color: '#555',
    fontSize: '12px',
  },
  errorText: {
    color: '#c0392b',
    fontSize: '12px',
    padding: '8px 0 0',
    fontWeight: 'bold',
  },
  statusRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
  },
  statusContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statusLabel: {
    color: '#333',
    fontSize: '14px',
  },
  offlineHint: {
    color: '#555',
    fontSize: '11px',
  },
  statusIndicator: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  statusDot: {
    width: '10px',
    height: '10px',
    borderRadius: '50%',
  },
  statusText: {
    color: '#000',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  debugRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: '1px solid rgba(0, 0, 0, 0.2)',
  },
  debugLabel: {
    color: '#333',
    fontSize: '14px',
  },
  debugValue: {
    color: '#555',
    fontSize: '12px',
    fontFamily: 'monospace',
  },
  dangerSection: {
    backgroundColor: 'rgba(239, 68, 68, 0.15)',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '16px',
    border: '2px solid #c0392b',
  },
  dangerTitle: {
    color: '#c0392b',
    fontSize: '12px',
    fontWeight: 'bold',
    marginBottom: '12px',
    textTransform: 'uppercase',
    letterSpacing: '1px',
  },
  dangerContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  dangerInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  dangerLabel: {
    color: '#333',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  dangerHint: {
    color: '#555',
    fontSize: '12px',
  },
  deleteButton: {
    backgroundColor: '#ef4444',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 16px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#FEAC64',
    borderRadius: '16px',
    padding: '24px',
    maxWidth: '350px',
    width: '100%',
    textAlign: 'center',
    border: '3px solid #333',
  },
  modalIcon: {
    fontSize: '48px',
    color: '#c0392b',
    fontWeight: 'bold',
    marginBottom: '12px',
  },
  modalTitle: {
    color: '#333',
    fontSize: '20px',
    margin: '0 0 12px 0',
  },
  modalBody: {
    textAlign: 'left',
    marginBottom: '16px',
  },
  modalText: {
    color: '#555',
    fontSize: '14px',
    margin: '0 0 12px 0',
  },
  modalList: {
    color: '#555',
    fontSize: '14px',
    margin: '0 0 12px 0',
    paddingLeft: '20px',
  },
  modalWarning: {
    color: '#c0392b',
    fontSize: '14px',
    fontWeight: 'bold',
    margin: 0,
  },
  errorBox: {
    backgroundColor: 'rgba(239, 68, 68, 0.2)',
    border: '2px solid #c0392b',
    borderRadius: '8px',
    padding: '12px',
    color: '#c0392b',
    fontSize: '14px',
    marginBottom: '16px',
    textAlign: 'left',
  },
  modalButtons: {
    display: 'flex',
    gap: '12px',
  },
  modalCancelButton: {
    flex: 1,
    backgroundColor: '#1e8449',
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  modalConfirmButton: {
    flex: 1,
    backgroundColor: '#000',
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  modalDeleteButton: {
    flex: 1,
    backgroundColor: '#c0392b',
    color: '#fff',
    border: '2px solid #333',
    borderRadius: '8px',
    padding: '12px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  buttonDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
};

export default ArcadeSettings;
